import { cx } from '@/lib/format';
import type { Technology } from '@/lib/types';
import { TECH_ICON_PATHS } from './techIcons';

/**
 * Fallback for technologies with no brand mark of their own (SQL, "Paper /
 * Spigot API" variants). Two letters stay legible at the sizes used here.
 */
function monogram(name: string): string {
  const overrides: Record<string, string> = {
    JavaScript: 'JS',
    TypeScript: 'TS',
    PostgreSQL: 'PG',
    'Tailwind CSS': 'TW',
    'HTML & CSS': 'H5',
    'Git & GitHub': 'GH',
    'Linux / VPS': 'LX',
    'Paper / Spigot API': 'PA',
    'AI-assisted development': 'AI',
  };
  if (overrides[name]) return overrides[name];

  const words = name.replace(/[^A-Za-z0-9\s/&.-]/g, '').split(/[\s/&-]+/).filter(Boolean);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

const SIZES = {
  sm: { box: 'h-7 w-7', icon: 'h-3.5 w-3.5', text: 'text-[9px]' },
  md: { box: 'h-10 w-10', icon: 'h-5 w-5', text: 'text-[11px]' },
  lg: { box: 'h-14 w-14', icon: 'h-7 w-7', text: 'text-sm' },
} as const;

type Size = keyof typeof SIZES;

interface TechChipProps {
  tech: Pick<Technology, 'id' | 'name' | 'slug' | 'color'>;
  size?: Size;
}

/** Official brand mark where one exists, monogram otherwise. */
export function TechChip({ tech, size = 'md' }: TechChipProps) {
  const color = tech.color ?? 'rgb(var(--c-faint))';
  const path = TECH_ICON_PATHS[tech.slug];
  const s = SIZES[size];

  return (
    <span
      className="group/chip relative inline-flex"
      style={{ '--tech': color } as React.CSSProperties}
      tabIndex={0}
      role="img"
      aria-label={tech.name}
    >
      <span
        className={cx(
          'grid shrink-0 place-items-center rounded-plate border',
          'border-line bg-elevated text-muted',
          'transition-colors duration-200 ease-smooth',
          'group-hover/chip:border-[var(--tech)] group-hover/chip:text-[var(--tech)]',
          'group-focus-visible/chip:border-[var(--tech)] group-focus-visible/chip:text-[var(--tech)]',
          s.box
        )}
      >
        {path ? (
          <svg viewBox="0 0 24 24" className={s.icon} fill="currentColor" aria-hidden focusable="false">
            <path d={path} />
          </svg>
        ) : (
          <span className={cx('font-mono font-semibold leading-none', s.text)}>
            {monogram(tech.name)}
          </span>
        )}
      </span>

      {/* pointer-events-none: the tooltip must never eat a click meant for the card. */}
      <span
        className="pointer-events-none absolute -top-1.5 left-1/2 z-40 -translate-x-1/2 -translate-y-full
                   whitespace-nowrap rounded-plate border border-line bg-canvas px-2 py-1.5
                   font-mono text-2xs text-ink opacity-0 shadow-lg
                   transition-all duration-200 ease-smooth
                   group-hover/chip:-translate-y-[calc(100%+3px)] group-hover/chip:opacity-100
                   group-focus-visible/chip:-translate-y-[calc(100%+3px)] group-focus-visible/chip:opacity-100"
        aria-hidden
      >
        {tech.name}
      </span>
    </span>
  );
}

export function TechChipRow({
  technologies,
  max = 99,
  size = 'sm',
}: {
  technologies: Technology[];
  max?: number;
  size?: Size;
}) {
  const shown = technologies.slice(0, max);
  const rest = technologies.length - shown.length;

  return (
    <div className="flex flex-wrap items-center gap-2">
      {shown.map((tech) => (
        <TechChip key={tech.id} tech={tech} size={size} />
      ))}
      {rest > 0 && (
        <span
          className={cx(
            'grid shrink-0 place-items-center rounded-plate border border-dashed border-line',
            'font-mono text-faint',
            SIZES[size].box,
            SIZES[size].text
          )}
          title={technologies.slice(max).map((tech) => tech.name).join(', ')}
        >
          +{rest}
        </span>
      )}
    </div>
  );
}
